import axios, {AxiosError} from "axios"
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query"
import {createContext, ReactNode, useEffect, useRef, useState} from "react"
import {useLocation, useNavigate} from "react-router-dom";
import {requestFirebaseNotificationPermission} from "../functions/firebase.ts";
import {IUser} from "../models/IUser.ts";

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL + '/api'

interface IAuthContextProps {
    registerMutation: any
    verifyEmailMutation: any
    loginMutation: any
    logoutMutation: any
    user?: IUser
    isLoggedIn: boolean
    isAdmin: boolean
    fetchingLoginStatus: boolean
    fetchLoginStateError: any
    registerError: string | null
    verifyMailError: string | null
    loginError: string | null
    logoutError: string | null
    setLoginError: (error: string | null) => void
    setRegisterError: (error: string | null) => void
}

const AuthContext = createContext<IAuthContextProps>({} as IAuthContextProps);

interface IAuthProviderProps {
    children: ReactNode;
}

export const AuthProvider = ({children}: IAuthProviderProps) => {
    const timeoutRef = useRef<any>(null);
    const [registerError, setRegisterError] = useState<string | null>(null);
    const [verifyMailError, setVerifyMailError] = useState<string | null>(null);
    const [loginError, setLoginError] = useState<string | null>(null);
    const [logoutError, setLogoutError] = useState<string | null>(null);
    const location = useLocation();
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const {data: loginStatus, isLoading: fetchingLoginStatus, error: fetchLoginStateError} = useQuery({
        queryKey: ['loginStatus'],
        retry: false,
        queryFn: () => axios.get(`${VITE_API_BASE_URL}/auth/status`, {
            withCredentials: true,
        }),
    })

    const isLoggedIn = fetchLoginStateError ? false : loginStatus?.data.isLoggedIn
    const isAdmin = fetchLoginStateError ? false : loginStatus?.data.isAdmin
    const user: IUser = fetchLoginStateError ? {} : loginStatus?.data.user

    const registerMutation = useMutation({
        mutationFn: async ({email, password, language}: {
            email: string,
            password: string,
            language: string
        }) => {
            await axios.post(`${VITE_API_BASE_URL}/auth/register`, {
                email,
                password,
                language
            }, {withCredentials: true})
        },
        onSuccess: () => {
            navigate('/verificationMailSend')
        },
        onError: (error: AxiosError) => {
            if (error.code == 'ERR_NETWORK') {
                setRegisterError(error.message)
            } else if (error.status == 400 && error.response) {
                const errorObject = error.response.data as any
                if (errorObject.errors) {
                    setRegisterError(errorObject.errors[0].msg)
                }
                if (errorObject.message) {
                    setRegisterError(errorObject.message)
                }
            } else if (error.status == 500 && error.response) {
                const errorObject = error.response.data as any
                setRegisterError(errorObject.message)
            }
        }
    })

    const verifyEmailMutation = useMutation({
        mutationFn: async (token: string) => {
            await axios.get(`${VITE_API_BASE_URL}/auth/verify-email/${token}`, {withCredentials: true})
        },
        onSuccess: () => {
            navigate('/login')
        },
        onError: (error: AxiosError) => {
            if (error.code == 'ERR_NETWORK') {
                setVerifyMailError(error.message)
            } else if (error.status == 400 && error.response) {
                const errorObject = error.response.data as any
                setVerifyMailError(errorObject.message)
            } else if (error.status == 500 && error.response) {
                const errorObject = error.response.data as any
                setVerifyMailError(errorObject.message)
            }
        }

    })

    const loginMutation = useMutation({
        mutationFn: async ({email, password}: {
            email: string;
            password: string
        }) => await axios.post(`${VITE_API_BASE_URL}/auth/login`, {
            email,
            password
        }, {withCredentials: true}),
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: ['loginStatus']})
            await queryClient.invalidateQueries({queryKey: ['cart']})
            await queryClient.invalidateQueries({queryKey: ["orders"]})

            setTimeout(() => {
                const redirectTo = location.state?.from || '/';
                navigate(redirectTo);
            }, 0)
        },
        onError: (error: AxiosError) => {
            if (error.code == 'ERR_NETWORK') {
                setLoginError(error.message)
            } else if (error.status == 400 && error.response) {
                const errorObject = error.response.data as any
                if (errorObject.errors) {
                    setLoginError(errorObject.errors[0].msg)
                }
                if (errorObject.message) {
                    setLoginError(errorObject.message)
                }
            } else if (error.status == 500 && error.response) {
                const errorObject = error.response.data as any
                setLoginError(errorObject.message)
            }
        }
    })

    const logoutMutation = useMutation({
        mutationFn: async () => await axios.post(`${VITE_API_BASE_URL}/auth/logout`, null,
            {
                withCredentials: true
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['loginStatus']})
            queryClient.invalidateQueries({queryKey: ['cart']})
            navigate("/")
        },
        onError: (error: AxiosError) => {
            if (error.code == 'ERR_NETWORK') {
                setLogoutError(error.message)
            } else if (error.status == 500 && error.response) {
                const errorObject = error.response.data as any
                setLogoutError(errorObject.message)
            }
        }
    })

    const refreshLoginMutation = useMutation({
        mutationFn: async () => await axios.post(`${VITE_API_BASE_URL}/auth/refresh`, null,
            {
                withCredentials: true,
            }),
        onSuccess: () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                logoutMutation.mutate();
            }, 30 * 60 * 1000);

            queryClient.invalidateQueries({queryKey: ['loginStatus']})
        },
        onError: (error: AxiosError) => {
            if (error.code == 'ERR_NETWORK') {
                console.error(`Error refreshing login:${error.message}`)
            }
            if (error.status === 403 && error.response) {
                const errorObject = error.response.data as any
                logoutMutation.mutate();
                console.error(`Error refreshing login:${errorObject.message}`)
            } else if (error.status == 401 && error.response) {
                const errorObject = error.response.data as any
                logoutMutation.mutate();
                console.error(`Error refreshing login:${errorObject.message}`)
            }
        }
    })

    useEffect(() => {
        if (isLoggedIn) {
            const refreshInterval = setInterval(() => {
                refreshLoginMutation.mutate();
            }, 5 * 60 * 1000);
            return () => clearInterval(refreshInterval);
        }
    }, [isLoggedIn, refreshLoginMutation, logoutMutation]);

    useEffect(() => {
        const savedToken = localStorage.getItem('fcmToken');
        if (savedToken || isAdmin) {
            requestFirebaseNotificationPermission();
        }
    }, [isAdmin]);

    return (
        <AuthContext.Provider
            value={{
                registerMutation,
                verifyEmailMutation,
                loginMutation,
                logoutMutation,
                user,
                isLoggedIn,
                isAdmin,
                fetchingLoginStatus,
                fetchLoginStateError,
                verifyMailError,
                registerError,
                loginError,
                logoutError,
                setLoginError,
                setRegisterError,
            }}>
            {children}
        </AuthContext.Provider>
    )
}

export default AuthContext